import { styleText } from 'node:util'

import * as p from '@clack/prompts'
import { readEnvVar } from '@core-engine/utils/env.js'
import type { BetterstartIntegrationId } from '@integration-engine/types.js'
import { persistBlobReadWriteToken } from '@next-adapter/init/scaffolders/env.js'
import {
  type CollectedIntegrationConfig,
  collectIntegrationConfig
} from '@next-adapter/integration-runtime.js'
import type { BetterstartPresetId } from '@preset-engine/types.js'

export type StorageMode = 'local' | 'r2' | 'vercel-blob' | 'railway-bucket'

export interface PresetPromptResult {
  presets: BetterstartPresetId[]
  integrations: BetterstartIntegrationId[]
  storage: StorageMode
  integrationConfig: CollectedIntegrationConfig
}

export interface PresetPromptOptions {
  /**
   * Auto-provisions a Vercel Blob store at selection time (init's Vercel CLI
   * flow: auth → reuse/create project link → create store → capture token).
   */
  provisionVercelBlob?: () => Promise<{
    ok: boolean
    token?: string
  }>
  provisionRailwayBucket?: () => Promise<{
    ok: boolean
    config?: CollectedIntegrationConfig
  }>
}

/**
 * Mask a Blob read-write token for display, mirroring the masked database URL
 * note: keep the recognizable `vercel_blob_rw_<storeId>` prefix (the store id
 * is also visible in the Vercel dashboard) and hide the secret tail.
 */
function maskBlobToken(token: string): string {
  const match = /^(vercel_blob_rw_[A-Za-z0-9]+)_/.exec(token)
  if (match) return `${match[1]}_***`
  return `${token.slice(0, 8)}***`
}

export async function promptPresets(
  cwd: string,
  options: PresetPromptOptions = {}
): Promise<PresetPromptResult> {
  const sections: CollectedIntegrationConfig['sections'] = []
  const overwriteKeys = new Set<string>()
  const mergeIntegrationConfig = (collected: CollectedIntegrationConfig): void => {
    sections.push(...collected.sections)
    for (const key of collected.overwriteKeys) {
      overwriteKeys.add(key)
    }
  }

  const storage = await p.select({
    message: 'Choose a file storage',
    options: [
      {
        value: 'vercel-blob' as const,
        label: 'Vercel Blob'
      },
      {
        value: 'r2' as const,
        label: 'Cloudflare R2'
      },
      {
        value: 'railway-bucket' as const,
        label: 'Railway Bucket',
        hint: 'Private S3-compatible storage with signed reads'
      },
      {
        value: 'local' as const,
        label: 'Local filesystem (public/media)',
        hint: '⚠ For testing only'
      }
    ],
    initialValue: 'vercel-blob' as const
  })

  if (p.isCancel(storage)) {
    p.cancel('Setup cancelled.')
    process.exit(0)
  }

  // Collect storage credentials immediately after the storage choice so input
  // is gathered up front, right where the user made the decision. Vercel Blob
  // auto-provisions through the Vercel CLI (like the database), creating a
  // project link if needed. A failed provisioning run never drops to a manual
  // token prompt — init continues with a blank BLOB_READ_WRITE_TOKEN; manual
  // entry exists when the Vercel flow is unavailable.
  if (storage === 'r2') {
    mergeIntegrationConfig(await collectIntegrationConfig(cwd, ['r2']))
  } else if (storage === 'railway-bucket') {
    const flow = await options.provisionRailwayBucket?.()
    if (flow?.ok && flow.config) {
      mergeIntegrationConfig(flow.config)
    } else if (flow) {
      p.log.warn(
        'Continuing without Railway bucket credentials — rerun betterstart add --integration railway-bucket after fixing Railway access.'
      )
    } else {
      mergeIntegrationConfig(await collectIntegrationConfig(cwd, ['railway-bucket']))
    }
  } else if (storage === 'vercel-blob') {
    const existingToken = readEnvVar(cwd, 'BLOB_READ_WRITE_TOKEN')?.trim()
    const flow =
      !existingToken && options.provisionVercelBlob
        ? await options.provisionVercelBlob()
        : undefined

    if (flow?.ok && flow.token) {
      // Persisted silently — like DATABASE_URL, no "Saved …" notice.
      persistBlobReadWriteToken(cwd, flow.token)
      sections.push({
        header: 'Storage (Vercel Blob)',
        vars: [{ key: 'BLOB_READ_WRITE_TOKEN', value: flow.token }]
      })
      overwriteKeys.add('BLOB_READ_WRITE_TOKEN')
    } else if (flow) {
      // Provisioning ran and failed (it already warned above). Never a manual
      // token prompt — continue with a blank key the user can fill in from
      // the store on vercel.com/dashboard/stores.
      p.log.warn(
        'Continuing without a Blob token — add BLOB_READ_WRITE_TOKEN to .env.local from the store on vercel.com/dashboard/stores.'
      )
      sections.push({
        header: 'Storage (Vercel Blob)',
        vars: [{ key: 'BLOB_READ_WRITE_TOKEN', value: '' }]
      })
    } else {
      if (existingToken) {
        p.log.info(
          `Using the existing BLOB_READ_WRITE_TOKEN from .env.local ${styleText(
            'dim',
            `(${maskBlobToken(existingToken)})`
          )}`
        )
      }
      // Reads an existing BLOB_READ_WRITE_TOKEN without prompting; with
      // Without automated provisioning, manual entry is the chosen path.
      mergeIntegrationConfig(await collectIntegrationConfig(cwd, ['vercel-blob']))
    }
  }

  const selectedPresets = await p.multiselect({
    message: 'Select presets',
    options: [
      {
        value: 'blog' as const,
        label: 'Blog',
        hint: 'Posts'
      }
    ],
    required: false,
    initialValues: ['blog' as const]
  })

  if (p.isCancel(selectedPresets)) {
    p.cancel('Setup cancelled.')
    process.exit(0)
  }

  const integrations: BetterstartIntegrationId[] = []

  // Email setup is deferred so the dashboard comes up in fewer steps: no
  // Resend prompt during init — `betterstart add --integration resend` (or
  // `--integrations resend`) sets it up later. Blank env keys are still
  // written so values can be filled in by hand; not in overwriteKeys, so
  // existing values are preserved.
  sections.push({
    header: 'Email (Resend)',
    vars: [
      { key: 'BETTERSTART_RESEND_API_KEY', value: '' },
      { key: 'BETTERSTART_EMAIL_FROM', value: '' }
    ]
  })

  if (storage === 'r2') {
    integrations.push('r2')
  } else if (storage === 'railway-bucket') {
    integrations.push('railway-bucket')
  } else if (storage === 'vercel-blob') {
    integrations.push('vercel-blob')
  }

  return {
    presets: selectedPresets,
    integrations,
    storage,
    integrationConfig: { sections, overwriteKeys }
  }
}
